import { useEffect } from 'react';
import { BOARD_WIDTH, BOARD_HEIGHT, shouldIgnoreKeyboardEvent, isYouTubeLink, getYouTubeId } from '../utils/whiteboardUtils';

export const useWhiteboardHotkeys = ({ setTool, setPenType, setSpacePressed, updatePan, panRef, undo, redo, shapes, selectedShape, selectedShapes, setShapes, setSelectedShape, setSelectedShapes, clipboardRef, createTextBlock, lastMousePosition }) => {
  useEffect(() => {
    const handleKeyDown = e => {
      if (shouldIgnoreKeyboardEvent(e)) return;
      if (e.code === 'Space' || e.key === 'h' || e.key === 'H') setSpacePressed(true);

      // Undo / Redo — must check before single-key shortcuts
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo?.();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo?.();
        return;
      }

      // Copy
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        const multi = selectedShapes?.length > 0;
        const single = !multi && selectedShape;
        if (!multi && !single) return;
        const copied = multi
          ? (shapes || []).filter(s => selectedShapes.includes(s.id))
          : [(shapes || []).find(s => s.id === selectedShape)].filter(Boolean);
        if (copied.length) clipboardRef.current = copied;
        return;
      }

      // Paste: shapes from internal clipboard, or text from system clipboard
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        if (document.activeElement?.isContentEditable) return; // let browser paste into active text block
        const items = clipboardRef?.current;
        if (!items || items.length === 0) {
          // No shapes — read system clipboard text
          e.preventDefault();
          navigator.clipboard.readText().then(text => {
            if (!text?.trim()) return;
            const trimmed = text.trim();
            const x = lastMousePosition?.current?.x || window.innerWidth / 2;
            const y = lastMousePosition?.current?.y || window.innerHeight / 2;
            if (isYouTubeLink(trimmed)) {
              const videoId = getYouTubeId(trimmed);
              if (videoId) {
                setShapes(prev => [...prev, {
                  id: Date.now(), type: 'youtube', x, y,
                  width: 560, height: 315, videoId, isPlaying: false, clickCount: 0,
                }]);
                return;
              }
            }
            createTextBlock?.(x, y, trimmed);
          }).catch(() => {});
          return;
        }
        e.preventDefault(); // block paste event so text isn't also inserted
        const now = Date.now();
        const newShapes = items.map((s, i) => ({
          ...s,
          id: now + i + Math.random(),
          x: s.x + 20,
          y: s.y + 20,
        }));
        setShapes(prev => [...prev, ...newShapes]);
        if (newShapes.length === 1) {
          setSelectedShape(newShapes[0].id);
          setSelectedShapes([]);
        } else {
          setSelectedShape(null);
          setSelectedShapes(newShapes.map(s => s.id));
        }
        return;
      }

      if (e.ctrlKey || e.metaKey) return; // ignore other Ctrl combos below

      if (e.key === 'v' || e.key === 'V') {
        e.preventDefault();
        setTool('select');
      } else if (e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        setTool('pen');
        setPenType('pen');
      } else if (e.key === 'h' || e.key === 'H') {
        e.preventDefault();
        setTool('highlighter');
        setPenType('highlighter');
      } else if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        setTool('eraser');
        setPenType('eraser');
      } else if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        setTool('shape');
      } else if (e.key === 'i' || e.key === 'I') {
        e.preventDefault();
        setTool('sticker');
      } else if (e.key === 't' || e.key === 'T') {
        e.preventDefault();
        setTool('text');
      }

      if (e.key === '+' || e.key === '=') {
        updatePan(p => ({ ...p, scale: Math.min(3, panRef.current.scale * 1.08) }));
      } else if (e.key === '-' || e.key === '_') {
        updatePan(p => ({ ...p, scale: Math.max(0.1, panRef.current.scale / 1.08) }));
      } else if (e.key === 'z' || e.key === 'Z') {
        updatePan({
          x: (window.innerWidth - BOARD_WIDTH * 1.0) / 2,
          y: (window.innerHeight - BOARD_HEIGHT * 1.0) / 2,
          scale: 1.0,
        });
      } else if (e.key === 'PageDown') {
        updatePan(p => ({ ...p, y: Math.max(p.y - 200, -(BOARD_HEIGHT * panRef.current.scale - window.innerHeight)) }));
      } else if (e.key === 'PageUp') {
        updatePan(p => ({ ...p, y: Math.min(p.y + 200, 0) }));
      } else if (e.key === 'ArrowLeft') {
        updatePan(p => ({ ...p, x: Math.min(p.x + 200, 0) }));
      } else if (e.key === 'ArrowRight') {
        updatePan(p => ({ ...p, x: Math.max(p.x - 200, -(BOARD_WIDTH * panRef.current.scale - window.innerWidth)) }));
      }
    };

    const handleKeyUp = e => {
      if (e.code === 'Space' || e.key === 'h' || e.key === 'H') setSpacePressed(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [undo, redo, shapes, selectedShape, selectedShapes]); // eslint-disable-line react-hooks/exhaustive-deps
};
